import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom, race, timer } from 'rxjs';
import { filter, first, map, tap } from 'rxjs/operators';
import { UserStore } from '@shared/stores/user.store';
import { User } from '@shared/models/users.model';

export const userGuard: CanActivateFn = async () => {
  const userStore = inject(UserStore);
  const router = inject(Router);

  try {
    // Convert the user signal to an observable
    const user$ = toObservable(userStore.user);

    // Create an observable that completes when the user companyId is available
    const userDataAvailable$ = user$.pipe(
      filter((user) => validateUserInfo(user)),
      first(),
      map(() => true),
    );

    const timeout = 90000;
    const timeout$ = timer(timeout).pipe(
      tap(() => {
        // TODO - implement a slow network popup or redirect to login page (TBD) - Does the OKTA Guard already defend against unauth scenarios?
        console.warn(`User data loading timed out after ${timeout}ms. Redirecting to home page.`);
        router.navigate(['/home']);
      }),
      map(() => false),
    );

    // Race between data availability and timeout
    return await firstValueFrom(
      race(userDataAvailable$, timeout$)
    );

  } catch (error) {
    console.error('Error in user guard:', error);
    router.navigate(['/home']);
    return false;
  }
};

function validateUserInfo(user: User): boolean {
  return !!user && !!user.companyId && user.companyId.length > 0 && !!user.email && user.email.length > 0;
}
